import { FormEvent, useState } from "react";
import AppLayout from "../../../../shared/components/AppLayout";
import AuthCard from "../../../../shared/components/AuthCard";
import Button from "../../../../shared/components/Button";
import PasswordInput from "../../../../shared/components/PasswordInput";
import Toast from "../../../../shared/components/Toast";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import { resetPassword } from "../../shared/services/authService";

type ResetPasswordPageProps = {
  token?: string;
};

const MIN_PASSWORD_LENGTH = 4;

export default function ResetPasswordPage({ token }: ResetPasswordPageProps) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!String(token || "").trim()) {
      setError(siteTexts.auth.resetPassword.invalidToken);
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(
        siteTexts.auth.resetPassword.minLength.replace(
          "{count}",
          String(MIN_PASSWORD_LENGTH),
        ),
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(siteTexts.auth.resetPassword.mismatch);
      return;
    }

    setSubmitting(true);
    try {
      const result = await resetPassword(String(token), newPassword);
      setSuccessMessage(
        result.message || siteTexts.auth.resetPassword.success,
      );
      window.sessionStorage.setItem("tnp-password-reset-success", "1");
      window.setTimeout(() => {
        window.location.hash = "#/login";
      }, 900);
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : siteTexts.auth.resetPassword.error,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout
      title={siteTexts.auth.resetPassword.title}
      subtitle={siteTexts.auth.resetPassword.subtitle}
    >
      {successMessage && (
        <Toast
          message={successMessage}
          variant="success"
          onClose={() => setSuccessMessage("")}
        />
      )}
      <AuthCard
        error={error}
        onSubmit={onSubmit}
        submitAction={
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting
              ? siteTexts.common.loading
              : siteTexts.auth.resetPassword.submit}
          </Button>
        }
        footer={
          <p>
            <a href="#/login">{siteTexts.auth.forgotPassword.backToLogin}</a>
          </p>
        }
      >
        <label className="field">
          <span>{siteTexts.auth.resetPassword.newPassword}</span>
          <PasswordInput
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            minLength={MIN_PASSWORD_LENGTH}
            required
          />
        </label>
        <label className="field">
          <span>{siteTexts.auth.resetPassword.confirmPassword}</span>
          <PasswordInput
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            minLength={MIN_PASSWORD_LENGTH}
            required
          />
        </label>
      </AuthCard>
    </AppLayout>
  );
}
